// Where the wall-count readout sits. The invite dock's compact pill is pinned
// top-left, exactly where the readout lives, so while the pill is visible the
// readout drops just below it — the one per-game accommodation the pill needs.
// Pure geometry, so it's testable without a canvas or a DOM.

import type { Rect } from '../layout/types.js';
import { WALL_COUNT_PADDING_PX, WALL_COUNT_PILL_GAP_PX } from '../utils/constants.js';

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** The slice of a `DOMRect` the conversion reads, so tests can pass literals. */
export interface VerticalBox {
  readonly top: number;
  readonly bottom: number;
  readonly height: number;
}

/**
 * The pill's bottom edge in the canvas's drawing space. The footprint is a
 * viewport rect in CSS px; renderers draw in CSS px too (the context carries
 * the devicePixelRatio transform, see `applyCanvasLayout`), so the only
 * conversion is the canvas's own on-page offset plus any CSS scaling between
 * its layout height and its on-page box.
 */
export function pillBottomInCanvas(
  footprint: Pick<VerticalBox, 'bottom'>,
  canvasBox: VerticalBox,
  canvasCssHeight: number,
): number {
  const scale = canvasBox.height > 0 ? canvasCssHeight / canvasBox.height : 1;
  return (footprint.bottom - canvasBox.top) * scale;
}

/** Top-left text origin; `pillBottom` is null whenever the pill isn't showing. */
export function wallCountOrigin(table: Rect, pillBottom: number | null): Point {
  const x = table.x + WALL_COUNT_PADDING_PX;
  const y = table.y + WALL_COUNT_PADDING_PX;
  if (pillBottom === null) return { x, y };
  return { x, y: Math.max(y, pillBottom + WALL_COUNT_PILL_GAP_PX) };
}
